import React, { useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import useReveal from '../../AIEmployees/useReveal';
import { BENCHMARK_MONTH } from '../../../content/benchmarkClaim';
import '../../AIEmployees/sections/AIEFaq.css';

/**
 * SAFaq — the FAQ beat on /super-agents.
 *
 * Two jobs (same pattern as AIEFaq):
 *  1. UX: answer what a visitor actually asks before trying the super-agent.
 *  2. SEO/GEO: the parent page renders FAQPage JSON-LD from THIS exact list
 *     (SA_FAQ_ITEMS), so structured data always matches visible content.
 *
 * Keyword strategy (Rhea, Aug 4): primary commercial target = "AI super
 * agent" (the unqualified term's SERP is polluted by sports/insurance
 * meanings). The blog /blog/what-is-a-super-agent/ OWNS the informational
 * "what is a super agent" query (pos ~10-11) — this FAQ deliberately targets
 * ADJACENT intents and hands the informational query to the blog via the
 * anchor text of the visible link below the list.
 *
 * Content grounded only in real product behavior. Benchmark claim is dated
 * and routes proof to /benchmarks (own-domain receipts earn AI citations).
 */
export const SA_FAQ_ITEMS = [
  {
    question: 'What is an AI super agent?',
    answer:
      'An AI super agent is a single AI agent that takes a task all the way to a finished result across every kind of work: research, coding, writing, data analysis, and media. Instead of one tool for chat, another for code, and another for video, it reasons through the whole job and delivers the finished artifact.',
  },
  {
    question: 'How is a super agent different from ChatGPT?',
    answer:
      "ChatGPT's agent features run inside a single session: when the conversation ends, the work stops. CellCog's super agent completes multi-step work with real tools (a terminal, a browser, and, with Cowork, your own machine) and returns finished artifacts like a cited research report, a working app, or an edited video. And when you need standing work on a schedule, the same engine powers CellCog's AI employees.",
  },
  {
    question: "What can CellCog's super agent produce?",
    answer:
      'Deep research with citations, code, video, images, speech, music, PDFs, presentations, spreadsheets, web apps, dashboards, and 3D models. A single prompt can go all the way to a finished deliverable in whatever format the job calls for.',
  },
  {
    question: 'Is CellCog really #1 in deep research?',
    answer:
      `CellCog ranked #1 on DeepResearch Bench, the public benchmark for deep-research agents, as of ${BENCHMARK_MONTH}. The badge at the top of this page links to the live leaderboard, and cellcog.ai/benchmarks has the full methodology and results, so you can verify the current standing yourself.`,
  },
  {
    question: 'Is CellCog free to try?',
    answer:
      'Yes. You can try CellCog free with no credit card needed, and you get 200 free credits at signup to run your first real tasks.',
  },
  {
    question: 'Can it work directly on my computer?',
    answer:
      'Yes. With Cowork, CellCog connects to your machine and your Chrome browser, so it can read and edit real project files, run commands, and work inside your logged-in sessions. Sensitive actions wait for your approval.',
  },
];

const SAFaq = () => {
  const [ref, visible] = useReveal();
  const [open, setOpen] = useState(null);

  const toggle = (i) => setOpen((cur) => (cur === i ? null : i));

  return (
    <section className="sa-faq aie-faq aie-section" ref={ref} aria-labelledby="sa-faq-title">
      <div className={`aie-section-head aie-reveal ${visible ? 'is-visible' : ''}`}>
        <span className="aie-eyebrow">Questions</span>
        <h2 id="sa-faq-title" className="aie-section-title aie-display">
          Everything you&rsquo;re wondering
        </h2>
        <p className="aie-section-sub">
          The honest answers, in plain English.
        </p>
      </div>

      <div className={`aie-faq__list aie-reveal ${visible ? 'is-visible' : ''}`}>
        {SA_FAQ_ITEMS.map((item, i) => (
          <div
            key={item.question}
            className={`aie-faq__item ${open === i ? 'is-open' : ''}`}
          >
            <button
              type="button"
              className="aie-faq__q"
              aria-expanded={open === i}
              onClick={() => toggle(i)}
            >
              <span>{item.question}</span>
              <PlusOutlined className="aie-faq__icon" />
            </button>
            <div className="aie-faq__a-wrap" role="region">
              <p className="aie-faq__a">{item.answer}</p>
            </div>
          </div>
        ))}
      </div>

      <p className="sa-faq__more">
        Want the longer read?{' '}
        <a href="/blog/what-is-a-super-agent/">What is a super agent</a>
        {' '}&middot; Proof:{' '}
        <Link to="/benchmarks">DeepResearch Bench results</Link>
      </p>
    </section>
  );
};

export default SAFaq;
